import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, setAccessToken } from "@/lib/api";
import { Languages, Lock, User, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import "@/styles/marketplace.css";

const LOGO = "https://customer-assets.emergentagent.com/job_beauty-hub-manage/artifacts/bqmxhyxp_image.png";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t, lang, toggleLang } = useLanguage();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const isAr = lang === "ar";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error(t("required_field")); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { username, password });
      if (r.data?.access_token) setAccessToken(r.data.access_token);
      setUser(r.data.user);
      toast.success(isAr ? "أهلاً بعودتك!" : "Welcome back!");
      // Always land on the salon dashboard — that's the operator surface.
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("invalid_credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-[#FAF5F2]"
      data-testid="login-page"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Top bar: marketplace link + language toggle */}
      <div className="flex items-center justify-between p-5 lg:p-7">
        <Link
          to="/m"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-600 hover:text-[#7A1F3D] transition"
          data-testid="back-to-marketplace"
        >
          <ArrowLeft size={15} className={isAr ? "rotate-180" : ""} />
          {isAr ? "العودة للماركت بليس" : "Back to marketplace"}
        </Link>
        <button
          onClick={toggleLang}
          data-testid="language-toggle-login"
          className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-stone-300 hover:border-[#7A1F3D] text-sm font-semibold text-stone-700 bg-white transition"
        >
          <Languages size={15} />
          <span>{isAr ? "EN" : "ع"}</span>
        </button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md">
          {/* Single, big, clean logo on white — no dark background, shown once */}
          <div className="flex justify-center mb-10">
            <img
              src={LOGO}
              alt="iSaloon by Maham"
              className="h-44 md:h-56 object-contain drop-shadow-sm"
              data-testid="login-logo"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-stone-900">
              {isAr ? "تسجيل دخول" : "Sign in"}
            </h2>
            <p className="text-sm text-stone-500 mt-2">
              {isAr
                ? "أدخل بياناتك للوصول إلى لوحة إدارة صالونك"
                : "Enter your details to access your salon dashboard"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-2xl shadow-md border border-stone-200 p-6 md:p-7">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-600 mb-2 block">
                {isAr ? "اسم المستخدم" : "Username"}
              </label>
              <div className="relative">
                <User size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-stone-400" />
                <input
                  type="text"
                  className="w-full ps-10 pe-3 py-3 rounded-lg bg-white border border-stone-300 text-stone-800 text-sm outline-none focus:border-[#7A1F3D] focus:ring-2 focus:ring-[#7A1F3D]/15 transition"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="username-input"
                  autoComplete="username"
                  placeholder="admin"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-600 mb-2 block">
                {isAr ? "كلمة المرور" : "Password"}
              </label>
              <div className="relative">
                <Lock size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-stone-400" />
                <input
                  type="password"
                  className="w-full ps-10 pe-3 py-3 rounded-lg bg-white border border-stone-300 text-stone-800 text-sm outline-none focus:border-[#7A1F3D] focus:ring-2 focus:ring-[#7A1F3D]/15 transition"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="sign-in-button"
              className="w-full py-3.5 rounded-lg font-bold text-white mt-2 shadow-md disabled:opacity-60 transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #7A1F3D, #5C1730)" }}
            >
              {loading
                ? (isAr ? "جاري الدخول..." : "Signing in...")
                : (isAr ? "تسجيل دخول" : "Sign in")}
            </button>
          </form>

          <div className="text-center mt-8 text-[11px] text-stone-400 tracking-[0.15em] uppercase" data-testid="developed-by">
            {t("developed_by")}
          </div>
        </div>
      </div>
    </div>
  );
}
